// src/components/common/UserMenu.tsx

import { useState, useRef, useEffect } from "react";
import type { User } from "../../types/user.types";

interface UserMenuBaseProps {
  user: User | null;
  onLogout: () => void;
  children?: React.ReactNode;
  onlyLogout?: boolean;
  customTrigger?: React.ReactNode;
}

export function UserMenu({
  user,
  onLogout,
  children,
  onlyLogout,
  customTrigger,
}: UserMenuBaseProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={menuRef}
      className={`relative inline-block ${customTrigger ? "w-full" : ""}`}
    >
      {/* User Icon or Custom Trigger */}
      {customTrigger ? (
        <div onClick={() => setIsOpen(!isOpen)} className="cursor-pointer">
          {customTrigger}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          title="Mi perfil"
          className="flex items-center justify-center w-10 h-10 rounded-full 
      hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <svg
            className="w-6 h-6 text-gray-700"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </button>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={`absolute ${customTrigger ? "bottom-full left-0 mb-2" : "right-0 mt-2"} w-64 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-100 animate-in fade-in zoom-in duration-200 ease-out`}
        >
          {/* Nombre + email */}
          {!onlyLogout && user && (
            <div className="px-5 py-4 border-b border-gray-50 mb-1">
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900 truncate">
                    {user.name} {user.lastname || ""}
                  </span>
                  <span className="shrink-0 inline-flex items-center justify-center w-4 h-4 bg-green-100 rounded-full">
                    <svg
                      className="w-2.5 h-2.5 text-green-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                </div>
                <span className="text-xs text-gray-500 mt-0.5">
                  <span className="hover:text-[#009ef7] cursor-pointer">
                    {user.email}
                  </span>
                </span>
              </div>
            </div>
          )}

          {/* Botones específicos */}
          {!onlyLogout && children}

          {/* Separador */}
          {!onlyLogout && <div className="h-px bg-gray-100 my-1 mx-2"></div>}

          {/* Logout Button */}
          <div className="px-2">
            <button
              onClick={onLogout}
              className="flex items-center w-full px-3 py-2 text-sm font-semibold text-red-600 rounded-lg hover:bg-red-50 transition-colors group cursor-pointer"
            >
              <svg
                className="w-4 h-4 mr-3 text-red-400 group-hover:text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
